/*
 * Export helpers for the unified report engine (requirements doc 2026-09-09).
 *
 * The server sends column metadata with every report -- key, Arabic label,
 * unit, alignment, totals flag -- so ONE implementation of Excel, CSV and
 * print serves all fifty reports. Before this, each finance page carried its
 * own ~250-line copy of the same three exporters.
 *
 * THE UNIT FIELD IS THE WHOLE POINT. Amounts are stored in old-unit SYP and
 * must be divided by 100 for display; percentages, counts and durations must
 * not be. Only unit === "syp_stored" is ever divided, and only here and in
 * the table cell renderer.
 */
import { companyToday } from "@/util/companyTime";

export default {
  methods: {
    reportFileStamp() {
      // Final-version doc 2026-10-05: the company (Damascus) date, not the PC's.
      return companyToday();
    },

    /** Column label for exports: Arabic first, English as the fallback. */
    reportColumnLabel(column) {
      return column.label_ar || column.label_en || column.key;
    },

    /**
     * Display value for one cell. Returns a Number for money so Excel can
     * format and sum it, and a string everywhere else.
     */
    reportCellValue(column, raw) {
      if (raw === null || raw === undefined || raw === "") {
        return "";
      }
      switch (column.unit) {
        case "syp_stored":
          return Number(raw) / 100;
        case "percent":
        case "count":
        case "minutes":
          return Number(raw);
        default:
          return String(raw);
      }
    },

    /** Same value, rendered as text for the table and the print view. */
    reportCellText(column, raw) {
      if (raw === null || raw === undefined || raw === "") {
        return "—";
      }
      const value = this.reportCellValue(column, raw);
      switch (column.unit) {
        case "syp_stored":
          return this.reportNumber(value, 2);
        case "percent":
          return this.reportNumber(value, 2) + "%";
        case "count":
          return this.reportNumber(value, 0);
        case "minutes":
          return this.reportNumber(value, 1);
        default:
          return String(value);
      }
    },

    reportNumber(value, decimals) {
      const number = Number(value || 0);
      return number.toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    },

    /**
     * The Excel number format for a column, or null when the column is not a
     * number at all. Formatting every numeric cell as money (what this did
     * before) turned a count of 358 trips into "358.00" and a 12.5% share
     * into a bare "12.50" -- both read as amounts in a finance workbook.
     */
    reportNumberFormat(column) {
      switch (column.unit) {
        case "syp_stored":
          return "#,##0.00";
        case "percent":
          return '#,##0.00"%"';
        case "count":
          return "#,##0";
        case "minutes":
          return "#,##0.0";
        default:
          return null;
      }
    },

    /**
     * Every table in the report, flattened. A composite report (a
     * reconciliation or a dashboard) carries several sections, each with its
     * own columns; a plain report is one unnamed table.
     */
    reportTables(report) {
      if (report.sections && report.sections.length) {
        return report.sections.map((section) => ({
          title: section.title_ar || section.key,
          columns: section.columns || [],
          rows: section.rows || [],
          totals: section.totals || {},
        }));
      }
      return [
        {
          title: "",
          columns: report.columns || [],
          rows: report.rows || [],
          totals: report.totals || {},
        },
      ];
    },

    /** Metadata rows that head every export: what this is and how to read it. */
    reportMetaRows(report) {
      const rows = [[report.title_ar || report.title_en]];

      if (report.period) {
        rows.push([
          this.$t("reports.period"),
          (report.period.from || "—") + " → " + (report.period.to || "—"),
          this.$t("reports.dateBasis"),
          report.period.basis_label_ar || report.period.column || "",
        ]);
      }
      rows.push([this.$t("reports.generatedAt"), report.generated_at || ""]);

      (report.warnings || []).forEach((warning) => {
        rows.push([this.$t("reports.warning"), warning.ar || warning.en || ""]);
      });
      (report.notes || []).forEach((note) => {
        rows.push([this.$t("reports.note"), note.ar || note.en || ""]);
      });

      return rows;
    },

    /**
     * A real .xlsx built client-side, so Arabic and RTL survive. The server
     * CSV stays available for raw integrations.
     */
    reportExportExcel(report) {
      const tables = this.reportTables(report);

      return import(/* webpackChunkName: "xlsx" */ "xlsx-js-style").then(
        function (module) {
          const XLSX = module.default || module;
          const aoa = this.reportMetaRows(report).slice();
          // One entry per row of `aoa`, holding the Excel number format of
          // each cell. Built alongside the rows rather than derived from the
          // sheet afterwards, because a composite report's columns differ per
          // section and the cell itself no longer remembers which column --
          // and its unit -- it came from.
          const formats = aoa.map(() => []);
          const push = function (row, rowFormats) {
            aoa.push(row);
            formats.push(rowFormats || []);
          };

          tables.forEach(
            function (table) {
              if (!table.columns.length) {
                return;
              }
              const columnFormats = table.columns.map((column) =>
                this.reportNumberFormat(column)
              );
              push([]);
              if (table.title) {
                push([table.title]);
              }
              push(table.columns.map((column) => this.reportColumnLabel(column)));
              table.rows.forEach(
                function (row) {
                  push(
                    table.columns.map((column) =>
                      this.reportCellValue(column, row[column.key])
                    ),
                    columnFormats
                  );
                }.bind(this)
              );
              if (Object.keys(table.totals).length) {
                push(
                  table.columns.map(
                    function (column, index) {
                      if (index === 0) {
                        return this.$t("reports.total");
                      }
                      return Object.prototype.hasOwnProperty.call(
                        table.totals,
                        column.key
                      )
                        ? this.reportCellValue(column, table.totals[column.key])
                        : "";
                    }.bind(this)
                  ),
                  columnFormats
                );
              }
            }.bind(this)
          );

          const ws = XLSX.utils.aoa_to_sheet(aoa);

          for (let r = 0; r < formats.length; r += 1) {
            const rowFormats = formats[r];
            for (let c = 0; c < rowFormats.length; c += 1) {
              if (!rowFormats[c]) {
                continue;
              }
              const cell = ws[XLSX.utils.encode_cell({ r: r, c: c })];
              if (cell && typeof cell.v === "number") {
                cell.z = rowFormats[c];
              }
            }
          }

          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, "report");
          XLSX.writeFile(
            wb,
            report.slug + "-" + this.reportFileStamp() + ".xlsx"
          );
        }.bind(this)
      );
    },

    /** Opens the browser print dialog against a print-only rendering. */
    reportExportPrint() {
      window.print();
    },
  },
};
